import { useState, useEffect, useRef, useContext } from "react";
//Components
import {
  Modal,
  Box,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
} from "@material-ui/core";
import SortableTableHeader from "./SortableTableHeader";
import TableColumnShowHide from "./TableColumnShowHide";
import CircularProgress from "@material-ui/core/CircularProgress";
import { ResizableBox } from "react-resizable";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import { UserContext } from "../Contexts/UserContext";

//Fetching
import { useLazyQuery } from "@apollo/client";
import { GET_CALL_LOGS } from "../Queries/GetCallLogs";

//Styles
import { useStyles } from "../Styles/styles";
import "../Styles/resizable.css";

//Utils
import { numberSort, wordSort, dateSort } from "../utils/utils";
import useWindowSize from "../utils/window-size";
import moment from "moment";

const CallLogs = ({
  showCallLogs,
  setShowCallLogs,
  modalStyle,
  incidentId,
  incidentNumber,
  userId,
}) => {
  const windowSize = useWindowSize();
  const classes = useStyles();
  const [rows, setRows] = useState([]);
  const [
    getCallLogs,
    { loading: callLogsLoading, error: callLogsError, data: callLogsData },
  ] = useLazyQuery(GET_CALL_LOGS, {
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "network-only",
    variables: { originatorId: userId },
    onError: error => {
      console.error(JSON.stringify(error));
    },
  });

  useEffect(() => {
    if (showCallLogs) {
      getCallLogs({ variables: { incidentId: incidentId } });
    }
  }, [showCallLogs, getCallLogs, incidentId]);

  useEffect(() => {
    const parseCallLogs = data => {
      let tempRows = [];
      data.map(entry => {
        const { id, createdAt, logEntry } = entry;
        const code = entry.logCode ? entry.logCode.name : "";
        let officer = entry.deptAttribute
          ? `${entry?.deptAttribute?.ori?.abbreviation}-${entry?.deptAttribute?.badge}`
          : "";
        tempRows.push(createData(id, createdAt, officer, code, logEntry));
        return null;
      });
      return tempRows;
    };

    if (
      !callLogsLoading &&
      !callLogsError &&
      callLogsData &&
      callLogsData.initialCallInfo.logEntries.length > 0
    ) {
      let reversed = callLogsData.initialCallInfo.logEntries.filter(
        logEntry => !!logEntry?.logEntry
      );
      reversed.reverse();
      setRows(parseCallLogs(reversed));
    }
  }, [callLogsData, callLogsError, callLogsLoading]);

  const createData = (id, createdAt, officer, code, logEntry) => {
    return { id, createdAt, officer, code, logEntry };
  };

  const sortLabel = useRef();
  const [sortDirection, setSortDirection] = useState("asc");
  const [sortedColumn, setSortedColumn] = useState("");
  // const [sortId, setSortId] = useState('')

  const handleSort = (event, columnName, id) => {
    const currentDirection = sortDirection;
    let newDirection;
    setSortedColumn(columnName);
    // setSortId(id)
    if (currentDirection === "asc") {
      setSortDirection("desc");
      newDirection = "desc";
    } else {
      setSortDirection("asc");
      newDirection = "asc";
    }
    sortRows(newDirection, id);
  };

  const sortRows = (direction, id) => {
    let data = rows;
    if (id === "badge" || id === "carNumber" || id === "radioNumber") {
      numberSort(direction, id, data);
    } else if (id === "lastContactedAt") {
      dateSort(direction, id, data);
    } else {
      wordSort(direction, id, data);
    }
    setRows(data);
  };
  const [showColumn, setShowColumn] = useState({
    logDate: true,
    logTime: true,
    officer: true,
    code: true,
    logEntry: true,
  });

  const handleShowColumn = e => {
    const name = e.target.value;
    const bool = e.target.checked;

    setShowColumn(prevState => {
      return { ...prevState, [name]: bool };
    });
  };
  const { nightMode } = useContext(UserContext);

  const headers = [
    { title: "Log\xa0Date", idName: "logDate", show: showColumn.logDate },
    { title: "Log\xa0Time", idName: "logTime", show: showColumn.logTime },
    { title: "Officer", idName: "officer", show: showColumn.officer },
    { title: "Code", idName: "code", show: showColumn.code },
    { title: "Entry", idName: "logEntry", show: showColumn.logEntry },
  ];
  if (callLogsError) return <p>Error: {callLogsError}</p>;
  if (callLogsLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress data-cy="loading-circle" />
      </div>
    );
  }
  if (callLogsData) {
    return (
      <Modal
        open={showCallLogs}
        onClose={() => setShowCallLogs(false)}
        className={modalStyle}
        data-cy="call-log-modal"
      >
        <div style={{ marginBottom: "2vh" }}>
          <div
            style={{
              textAlign: "center",
              backgroundColor: "#5097d6",
              borderTopLeftRadius: "5px",
              borderTopRightRadius: "5px",
            }}
          >
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
              position="relative"
            >
              <h3
                data-cy="available-officers-title"
                style={{
                  display: "in-line block",
                  color: "white",
                  margin: "0px",
                }}
              >
                Call Logs for {incidentNumber}
              </h3>
              <TableColumnShowHide
                headers={headers}
                handleShowColumn={handleShowColumn}
                showClose={true}
                onCloseHandler={() => setShowCallLogs(false)}
              />
            </Box>
          </div>
          <ResizableBox
            width={windowSize.width * 0.78}
            height={windowSize.height * 0.5}
            handle={<DragHandleIcon className={`react-resizable-handle`} />}
            handleSize={[20, 20]}
            axis={"y"}
          >
            <TableContainer
              style={{ width: "100%" }}
              className={classes.tableContainer}
              component={Paper}
            >
              <Table
                stickyHeader
                className={classes.table}
                aria-label="simple table"
              >
                <TableHead>
                  <TableRow>
                    {headers.map(header => {
                      return (
                        <SortableTableHeader
                          show={header.show}
                          key={header.title}
                          title={header.title}
                          idName={header.idName}
                          handleSort={handleSort}
                          sortLabel={sortLabel}
                          sortedColumn={sortedColumn}
                          sortDirection={sortDirection}
                        />
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody data-cy="availOfficer-table-body">
                  {rows.map((row, index) => (
                    <TableRow
                      style={
                        index % 2
                          ? { background: nightMode ? "black" : "white" }
                          : { background: nightMode ? "black" : "#eee" }
                      }
                      data-cy="availOfficer-table-row"
                      key={row.id}
                    >
                      <TableCell
                        style={{ display: showColumn.logDate ? "" : "none" }}
                        scope="row"
                      >
                        {moment
                          .utc(row.createdAt, "YYYY-MM-DD HH:mm:ss UTC")
                          .local()
                          .format("MM/DD/YY")}
                      </TableCell>
                      <TableCell
                        style={{ display: showColumn.logTime ? "" : "none" }}
                        scope="row"
                      >
                        {moment
                          .utc(row.createdAt, "YYYY-MM-DD HH:mm:ss UTC")
                          .local()
                          .format("HH:mm")}
                      </TableCell>
                      <TableCell
                        style={{ display: showColumn.officer ? "" : "none" }}
                        scope="row"
                      >
                        {row.officer}
                      </TableCell>
                      <TableCell
                        style={{ display: showColumn.code ? "" : "none" }}
                        scope="row"
                      >
                        {row.code}
                      </TableCell>
                      <TableCell
                        style={{ display: showColumn.logEntry ? "" : "none" }}
                        scope="row"
                      >
                        {row.logEntry}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </ResizableBox>
        </div>
      </Modal>
    );
  } else {
    return null;
  }
};

export default CallLogs;
